import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/layout/MainLayout';
import { pendingOrderService } from '../../api/services/pendingOrderService';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Pencil, X, Check, Plus, Trash2 } from 'lucide-react';

export default function PendingOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmComment, setConfirmComment] = useState('');
  const [noteComment, setNoteComment] = useState('');
  const [editingOrdinal, setEditingOrdinal] = useState<number | null>(null);
  const [editingPartNumber, setEditingPartNumber] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Add item form
  const [showAddRow, setShowAddRow] = useState(false);
  const [newPart, setNewPart] = useState('');
  const [newQty, setNewQty] = useState(1);
  const [newPrice, setNewPrice] = useState('0.00');
  const [newDesc, setNewDesc] = useState<string | null>(null);
  const [lookingUpPart, setLookingUpPart] = useState(false);
  const newPartInputRef = useRef<HTMLInputElement>(null);

  // Inline qty/price editing
  const [editField, setEditField] = useState<{ ordinal: number; field: 'qty' | 'price' } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');

  const updatePartMutation = useMutation({
    mutationFn: ({ ordinal, partNumber }: { ordinal: number; partNumber: string }) =>
      pendingOrderService.updateItemPartNumber(Number(id), ordinal, partNumber),
    onSuccess: () => {
      toast.success('Part number updated');
      setEditingOrdinal(null);
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
    },
    onError: () => toast.error('Failed to update part number'),
  });

  const addItemMutation = useMutation({
    mutationFn: () => pendingOrderService.addItem(Number(id), {
      partNumber: newPart.trim().toUpperCase(),
      quantity: newQty,
      unitPrice: parseFloat(newPrice) || 0,
    }),
    onSuccess: () => {
      toast.success('Item added');
      setShowAddRow(false);
      setNewPart(''); setNewQty(1); setNewPrice('0.00'); setNewDesc(null);
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
    },
    onError: () => toast.error('Failed to add item'),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (ordinal: number) => pendingOrderService.deleteItem(Number(id), ordinal),
    onSuccess: () => {
      toast.success('Item removed');
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
    },
    onError: () => toast.error('Failed to remove item'),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ ordinal, data }: { ordinal: number; data: { quantity?: number; unitPrice?: number } }) =>
      pendingOrderService.updateItem(Number(id), ordinal, data),
    onSuccess: () => {
      toast.success('Updated');
      setEditField(null);
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
    },
    onError: () => toast.error('Failed to update'),
  });

  const lookupPartForAdd = async (partNumber: string) => {
    const trimmed = partNumber.trim().toUpperCase();
    if (!trimmed) { setNewDesc(null); return; }
    setLookingUpPart(true);
    try {
      const info = await pendingOrderService.getPartInfo(trimmed);
      setNewDesc(info.description);
    } catch {
      setNewDesc(null);
    } finally {
      setLookingUpPart(false);
    }
  };

  const saveFieldEdit = () => {
    if (!editField) return;
    const val = parseFloat(editFieldValue);
    if (isNaN(val)) { setEditField(null); return; }
    if (editField.field === 'qty') {
      const qty = Math.round(val);
      if (qty < 1) { setEditField(null); return; }
      updateItemMutation.mutate({ ordinal: editField.ordinal, data: { quantity: qty } });
    } else {
      if (val < 0) { setEditField(null); return; }
      updateItemMutation.mutate({ ordinal: editField.ordinal, data: { unitPrice: val } });
    }
  };

  const startEdit = (ordinal: number, currentPartNumber: string) => {
    setEditingOrdinal(ordinal);
    setEditingPartNumber(currentPartNumber);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const saveEdit = () => {
    if (editingOrdinal === null) return;
    const trimmed = editingPartNumber.trim().toUpperCase();
    if (!trimmed) return;
    updatePartMutation.mutate({ ordinal: editingOrdinal, partNumber: trimmed });
  };

  const { data: order, isLoading } = useQuery({
    queryKey: ['pending-order', id],
    queryFn: () => pendingOrderService.getById(Number(id)),
    enabled: !!id,
  });

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [shippingForm, setShippingForm] = useState({
    ShipToName: '', ShipToAddress1: '', ShipToAddress2: '',
    ShipToCity: '', ShipToState: '', ShipToZipCode: '', ShipToCountry: '', ShipVia: '',
  });

  const updateShippingMutation = useMutation({
    mutationFn: (data: typeof shippingForm) => pendingOrderService.updateShipping(Number(id), data),
    onSuccess: () => {
      toast.success('Shipping info updated');
      setIsEditingShipping(false);
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
    },
    onError: () => toast.error('Failed to update shipping info'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => pendingOrderService.cancel(Number(id)),
    onSuccess: () => {
      toast.success('Order cancelled');
      queryClient.invalidateQueries({ queryKey: ['pending-orders'] });
      navigate('/orders/pending');
    },
    onError: () => toast.error('Failed to cancel order'),
  });

  const confirmMutation = useMutation({
    mutationFn: () => pendingOrderService.confirm(Number(id), confirmComment),
    onSuccess: () => {
      toast.success('Order confirmed successfully');
      queryClient.invalidateQueries({ queryKey: ['pending-orders'] });
      navigate('/orders/pending');
    },
    onError: () => toast.error('Failed to confirm order'),
  });

  const logMutation = useMutation({
    mutationFn: () => pendingOrderService.addLog(Number(id), noteComment),
    onSuccess: () => {
      toast.success('Note added');
      setNoteComment('');
      queryClient.invalidateQueries({ queryKey: ['pending-order', id] });
      queryClient.invalidateQueries({ queryKey: ['pending-orders'] });
    },
    onError: () => toast.error('Failed to add note'),
  });

  if (isLoading) {
    return (
      <MainLayout>
        <div className="px-4 py-8 text-center text-gray-500">Loading...</div>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <div className="px-4 py-8 text-center text-gray-500">Order not found.</div>
      </MainLayout>
    );
  }

  const subtotal = order.Items.reduce((sum, i) => sum + i.UnitPrice * i.Quantity, 0);
  const total = subtotal + order.ShippingTotal - order.Discount;
  const hasInvalidPartNumbers = order.Items.some(i => !i.PartNumberValid);

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/orders/pending')}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={16} /> Back to Orders
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Pending Order — SO# {order.CustomerOrderNo}
            </h2>
            <button
              onClick={() => setShowCancelConfirm(true)}
              disabled={cancelMutation.isPending}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
            >
              Cancel &amp; Close Order
            </button>
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCancelConfirm(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Cancel Order?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This will close pending order <strong>{order.CustomerOrderNo}</strong> and log your name as the canceller. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Go Back
                </button>
                <button
                  onClick={() => { setShowCancelConfirm(false); cancelMutation.mutate(); }}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400"
                >
                  {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel Order'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* InSystem Warning */}
        {order.InSystem && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded">
            <strong>Error:</strong> PO Number <strong>{order.CustomerOrderNo}</strong> is already in the system.
          </div>
        )}

        {/* Order Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Order Details</h3>
            <dl className="space-y-1 text-sm">
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Customer:</dt><dd className="text-gray-900 dark:text-gray-100">{order.CustomerId}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">PO Number:</dt><dd className="text-gray-900 dark:text-gray-100 font-mono">{order.CustomerOrderNo}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Order Date:</dt><dd className="text-gray-900 dark:text-gray-100">{format(new Date(order.OrderDate), 'M/d/yyyy')}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Email:</dt><dd className="text-gray-900 dark:text-gray-100">{order.CustomerEmail}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Ship Via:</dt><dd className="text-gray-900 dark:text-gray-100">{isEditingShipping ? <span className="italic text-gray-400">see Ship To</span> : order.ShipVia}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Terms:</dt><dd className="text-gray-900 dark:text-gray-100">{order.Terms}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Drop Ship:</dt><dd className="text-gray-900 dark:text-gray-100">{order.DropShip ? 'Yes' : 'No'}</dd></div>
              {order.Note && <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32">Note:</dt><dd className="text-gray-900 dark:text-gray-100">{order.Note}</dd></div>}
            </dl>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Ship To</h3>
              {!isEditingShipping ? (
                <button
                  onClick={() => {
                    setShippingForm({
                      ShipToName: order.ShipToName || '',
                      ShipToAddress1: order.ShipToAddress1 || '',
                      ShipToAddress2: order.ShipToAddress2 || '',
                      ShipToCity: order.ShipToCity || '',
                      ShipToState: order.ShipToState || '',
                      ShipToZipCode: order.ShipToZipCode || '',
                      ShipToCountry: order.ShipToCountry || '',
                      ShipVia: order.ShipVia || '',
                    });
                    setIsEditingShipping(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => updateShippingMutation.mutate(shippingForm)}
                    disabled={updateShippingMutation.isPending}
                    className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                    title="Save"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => setIsEditingShipping(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            {isEditingShipping ? (
              <div className="space-y-2 text-sm">
                {([
                  ['ShipVia',       'Ship Via',  'text'],
                  ['ShipToName',    'Name',      'text'],
                  ['ShipToAddress1','Address 1', 'text'],
                  ['ShipToAddress2','Address 2', 'text'],
                  ['ShipToCity',    'City',      'text'],
                  ['ShipToState',   'State',     'text'],
                  ['ShipToZipCode', 'ZIP',       'text'],
                  ['ShipToCountry', 'Country',   'text'],
                ] as [keyof typeof shippingForm, string, string][]).map(([field, label]) => (
                  <div key={field} className="flex items-center gap-2">
                    <label className="w-24 text-gray-500 dark:text-gray-400 shrink-0">{label}</label>
                    <input
                      type="text"
                      value={shippingForm[field]}
                      onChange={e => setShippingForm(f => ({ ...f, [field]: e.target.value }))}
                      className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-900 dark:text-gray-100 space-y-0.5">
                <div className="font-medium">{order.ShipToName}</div>
                <div>{order.ShipToAddress1}</div>
                {order.ShipToAddress2 && <div>{order.ShipToAddress2}</div>}
                <div>{order.ShipToCity}, {order.ShipToState} {order.ShipToZipCode}</div>
                <div>{order.ShipToCountry}</div>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Order Items</h3>
            {!showAddRow && (
              <button
                onClick={() => { setShowAddRow(true); setTimeout(() => newPartInputRef.current?.focus(), 0); }}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                <Plus size={12} /> Add Item
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {['#', 'Part Number / Description', 'Qty', 'Unit Price', 'Line Total', ''].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {order.Items.length === 0 && !showAddRow && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                      No items — click "Add Item" to add line items manually.
                    </td>
                  </tr>
                )}
                {order.Items.map(item => (
                  <tr key={item.Ordinal} className="hover:bg-gray-50 dark:hover:bg-gray-700 group">
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 align-top">{item.Ordinal}</td>
                    <td className="px-4 py-2 align-top">
                      {editingOrdinal === item.Ordinal ? (
                        <div className="flex items-center gap-1">
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingPartNumber}
                            onChange={e => setEditingPartNumber(e.target.value.toUpperCase())}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEdit();
                              if (e.key === 'Escape') setEditingOrdinal(null);
                            }}
                            onBlur={saveEdit}
                            className="w-40 px-2 py-1 text-sm font-mono border border-blue-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={updatePartMutation.isPending}
                          />
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-1 group/part cursor-pointer"
                          onClick={() => startEdit(item.Ordinal, item.PartNumber)}
                        >
                          <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">{item.PartNumber}</span>
                          <Pencil size={12} className="text-gray-400 opacity-0 group-hover/part:opacity-100 transition-opacity" />
                          {!item.PartNumberValid && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded">
                              Not in Shiva
                            </span>
                          )}
                        </div>
                      )}
                      {item.Description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.Description}</div>
                      )}
                      {(item.SeatRow || item.SeatType || item.VehicleModels) && (
                        <div className="text-xs text-blue-700 dark:text-blue-400 mt-1 leading-relaxed">
                          {[item.SeatRow, item.SeatType, item.VehicleModels, item.Extras]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 text-center align-top">
                      {editField?.ordinal === item.Ordinal && editField.field === 'qty' ? (
                        <input
                          type="number"
                          value={editFieldValue}
                          min={1}
                          onChange={e => setEditFieldValue(e.target.value)}
                          onBlur={saveFieldEdit}
                          onKeyDown={e => { if (e.key === 'Enter') saveFieldEdit(); if (e.key === 'Escape') setEditField(null); }}
                          autoFocus
                          className="w-14 px-1 py-0.5 text-sm text-center border border-blue-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={updateItemMutation.isPending}
                        />
                      ) : (
                        <span
                          className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          onClick={() => { setEditField({ ordinal: item.Ordinal, field: 'qty' }); setEditFieldValue(String(item.Quantity)); }}
                          title="Click to edit"
                        >
                          {item.Quantity}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 align-top">
                      {editField?.ordinal === item.Ordinal && editField.field === 'price' ? (
                        <input
                          type="number"
                          value={editFieldValue}
                          min={0}
                          step={0.01}
                          onChange={e => setEditFieldValue(e.target.value)}
                          onBlur={saveFieldEdit}
                          onKeyDown={e => { if (e.key === 'Enter') saveFieldEdit(); if (e.key === 'Escape') setEditField(null); }}
                          autoFocus
                          className="w-20 px-1 py-0.5 text-sm border border-blue-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={updateItemMutation.isPending}
                        />
                      ) : (
                        <span
                          className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          onClick={() => { setEditField({ ordinal: item.Ordinal, field: 'price' }); setEditFieldValue(item.UnitPrice.toFixed(2)); }}
                          title="Click to edit"
                        >
                          ${item.UnitPrice.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-medium align-top">
                      ${(item.UnitPrice * item.Quantity).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <button
                        onClick={() => deleteItemMutation.mutate(item.Ordinal)}
                        disabled={deleteItemMutation.isPending}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30"
                        title="Remove item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}

                {/* Add item inline row */}
                {showAddRow && (
                  <tr className="bg-indigo-50 dark:bg-indigo-900/20">
                    <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500 align-top">—</td>
                    <td className="px-4 py-2 align-top">
                      <input
                        ref={newPartInputRef}
                        type="text"
                        value={newPart}
                        onChange={e => setNewPart(e.target.value.toUpperCase())}
                        onBlur={() => lookupPartForAdd(newPart)}
                        placeholder="Part number"
                        className="w-40 px-2 py-1 text-sm font-mono border border-indigo-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={addItemMutation.isPending}
                      />
                      {lookingUpPart && <div className="text-xs text-gray-400 mt-0.5">Looking up...</div>}
                      {newDesc && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{newDesc}</div>}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <input
                        type="number"
                        value={newQty}
                        min={1}
                        onChange={e => setNewQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-14 px-1 py-1 text-sm text-center border border-indigo-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={addItemMutation.isPending}
                      />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <input
                        type="number"
                        value={newPrice}
                        min={0}
                        step={0.01}
                        onChange={e => setNewPrice(e.target.value)}
                        className="w-20 px-1 py-1 text-sm border border-indigo-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={addItemMutation.isPending}
                      />
                    </td>
                    <td className="px-4 py-2 align-top">
                      <div className="flex gap-1">
                        <button
                          onClick={() => addItemMutation.mutate()}
                          disabled={!newPart.trim() || addItemMutation.isPending}
                          className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {addItemMutation.isPending ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          onClick={() => { setShowAddRow(false); setNewPart(''); setNewQty(1); setNewPrice('0.00'); setNewDesc(null); }}
                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-sm text-right font-medium text-gray-700 dark:text-gray-300">Subtotal:</td>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">${subtotal.toFixed(2)}</td>
                </tr>
                {order.ShippingTotal > 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-sm text-right font-medium text-gray-700 dark:text-gray-300">Shipping:</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">${order.ShippingTotal.toFixed(2)}</td>
                  </tr>
                )}
                {order.Discount > 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-sm text-right font-medium text-gray-700 dark:text-gray-300">Discount:</td>
                    <td className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400">-${order.Discount.toFixed(2)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-sm text-right font-bold text-gray-900 dark:text-gray-100">Total:</td>
                  <td className="px-4 py-2 text-sm font-bold text-gray-900 dark:text-gray-100">${total.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Log / Activity */}
        {order.Log.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Activity Log</h3>
            <div className="space-y-2">
              {order.Log.map(entry => (
                <div key={entry.Id} className="text-sm text-gray-700 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600 pl-3">
                  <span className="text-gray-500 dark:text-gray-400">
                    {format(new Date(entry.CreateDate), 'M/d/yy h:mm a')} — {entry.CreateBy}:
                  </span>{' '}
                  {entry.Comment}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Note */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Add Note</h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={noteComment}
              onChange={e => setNoteComment(e.target.value)}
              placeholder="Add a note..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={() => logMutation.mutate()}
              disabled={!noteComment.trim() || logMutation.isPending}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            >
              {logMutation.isPending ? 'Saving...' : 'Add Note'}
            </button>
          </div>
        </div>

        {/* Confirm */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Confirm Order</h3>
          {hasInvalidPartNumbers && (
            <div className="mb-3 bg-red-50 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm">
              <strong>Error:</strong> One or more part numbers are not in Shiva. Fix or remove the flagged item(s) before confirming.
            </div>
          )}
          <div className="space-y-3">
            <textarea
              value={confirmComment}
              onChange={e => setConfirmComment(e.target.value)}
              placeholder="Order comment (required)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => navigate('/orders/pending')}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmMutation.mutate()}
                disabled={!confirmComment.trim() || order.InSystem || hasInvalidPartNumbers || confirmMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                {confirmMutation.isPending ? 'Confirming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>

      </div>
    </MainLayout>
  );
}
